import { useRuntimeVariables } from 'app-kit'
import { AxiosError } from 'axios'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { useKestrel } from 'features/kestrelViewer'
import { useMemo } from 'react'
import useSWR from 'swr'
import {
  FindNearbyPointsRequest,
  FindNearbyPointsResponse,
  PhotoResponse,
  UseKestrelNearbyPhotosResult,
  WorldPosition,
} from 'types'

export const useKestrelNearbyPhotos = (
  modelId: string | undefined,
  position: WorldPosition | undefined
): UseKestrelNearbyPhotosResult => {
  const { isAuthenticated } = useGeckoAuth()
  const { authorizedAxios } = useKestrel()
  const { KESTREL_API_URL } = useRuntimeVariables()

  const requestBody = useMemo(
    () =>
      modelId && position
        ? {
            model_id: modelId,
            position,
          }
        : null,
    [modelId, position]
  )

  const fetcher = async (body: FindNearbyPointsRequest) => {
    try {
      const response = await authorizedAxios.post(
        `${KESTREL_API_URL}/api/v1/find-nearby-points`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      return response.data as FindNearbyPointsResponse
    } catch (error) {
      const errorMessage = `Failed to fetch nearby photos from Kestrel API for model ${body.model_id}`
      console.error(errorMessage, error)
      const enhancedError = new Error(errorMessage)
      enhancedError.cause =
        error instanceof AxiosError ? error.response?.data : error
      throw enhancedError
    }
  }

  const { data, error, isLoading } = useSWR<FindNearbyPointsResponse>(
    isAuthenticated && requestBody
      ? ['kestrel-nearby-photos', requestBody]
      : null,
    () => fetcher(requestBody!),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )

  return {
    photoResponses: data?.photos || [],
    isLoading,
    error,
  }
}

export const fetchNearbyPhotos = async (
  modelId: string,
  position: WorldPosition,
  authorizedAxios: AuthedAxiosRequests,
  kestrelApiUrl: string
): Promise<PhotoResponse[]> => {
  const requestBody = {
    model_id: modelId,
    position,
  }

  try {
    const response = await authorizedAxios.post(
      `${kestrelApiUrl}/api/v1/photos-from-point`,
      requestBody,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    const data: PhotoResponse[] = response.data
    return data
  } catch (error) {
    const errorMessage = `Failed to fetch nearby photos from Kestrel API for model ${modelId}`
    console.error(errorMessage, error)
    throw new Error(errorMessage)
  }
}
